import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// Attach Authorization header from sessionStorage as a fallback when httpOnly cookies
// aren't sent (cross-origin without credentials). Primary auth is the httpOnly cookie
// set by the backend; sessionStorage is cleared on tab close (safer than localStorage).
// Mehr-Standort Phase 4 — Werkstatt-Filial-Umschalter. Ein Hauptsitz kann den
// Werkstatt-Kontext auf eine Filiale umschalten; der Header wird nur gesetzt,
// solange eine Werkstatt-Seite aktiv ist (set on mount / clear on unmount).
let _apiOrgBranch = null;
export function setApiOrgBranch(id) {
    _apiOrgBranch = id || null;
    // Jun-2026 (User-Wunsch): globalen Filial-Kontext melden, damit ein sichtbarer
    // „Standort"-Badge im Header (modulübergreifend) reaktiv mitläuft.
    try {
        window.dispatchEvent(new CustomEvent("tw-branch-changed", { detail: { id: _apiOrgBranch } }));
    } catch { /* SSR/Non-Browser */ }
}
export function getApiOrgBranch() { return _apiOrgBranch; }

// P1 Werkstatt (Jun-2026): Arbeitgeber-Kontext für Mitarbeiter (Vermittler), die
// im Firmen-Bereich in den echten Händler-Modulen arbeiten. Wird in sessionStorage
// persistiert, damit der Kontext bei Navigation/Reload erhalten bleibt; ein
// sichtbares Banner + „Zurück" auf der Modulseite beendet ihn wieder.
let _apiEmployer = sessionStorage.getItem("tw_employer_id") || null;
export function setApiEmployer(id) {
    _apiEmployer = id || null;
    try {
        if (_apiEmployer) sessionStorage.setItem("tw_employer_id", _apiEmployer);
        else sessionStorage.removeItem("tw_employer_id");
        window.dispatchEvent(new CustomEvent("tw-employer-changed", { detail: { id: _apiEmployer } }));
    } catch { /* SSR/Non-Browser */ }
}
export function getApiEmployer() { return _apiEmployer; }

api.interceptors.request.use((config) => {
    const token = sessionStorage.getItem("tw_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Forward the short-lived re-auth token (settings unlock). The backend
    // uses it via the `require_reauth` dependency on sensitive endpoints.
    const reauthToken = sessionStorage.getItem("tw_reauth_token");
    if (reauthToken) {
        config.headers["X-Reauth-Token"] = reauthToken;
    }
    if (_apiOrgBranch) {
        config.headers["X-Org-Branch"] = _apiOrgBranch;
    }
    if (_apiEmployer) {
        config.headers["X-Employer-Id"] = _apiEmployer;
    }
    return config;
});

// Jul-2026 (User-Wunsch) — Automatischer Logout: läuft die Sitzung ab (401),
// springt die App sofort zur Login-Seite (nur wenn vorher eine Sitzung bestand).
let _redirecting401 = false;
api.interceptors.response.use(
    (r) => r,
    (error) => {
        // Jul-2026 (Bugfix „Blackscreen") — FastAPI/Pydantic-Validierungsfehler
        // (detail = Array von Objekten) in einen String normalisieren, damit
        // toast.error(e.response?.data?.detail) nie ein Objekt rendert (React-Crash).
        const data = error?.response?.data;
        // Aug-2026 (User-Bug „Doppel-Scan-JSON-Toast"): strukturierte Fehler mit
        // maschinenlesbarem `code` (z. B. possible_duplicate) NICHT zu String
        // wandeln — die Seiten werten det.code aus und zeigen eigene Dialoge.
        const _keepStructured = data && typeof data === "object" && data.detail
            && typeof data.detail === "object" && !Array.isArray(data.detail)
            && typeof data.detail.code === "string";
        if (data && typeof data === "object" && data.detail !== undefined
            && typeof data.detail !== "string" && !_keepStructured) {
            try { data.detail = formatApiErrorDetail(data.detail); } catch { data.detail = "Etwas ist schiefgelaufen."; }
        }
        const status = error?.response?.status;
        const reqUrl = String(error?.config?.url || "");
        const path = window.location.pathname;
        if (
            status === 401 && !_redirecting401
            && !reqUrl.includes("/auth/")
            && path !== "/login" && path !== "/" && !path.startsWith("/register")
            && (sessionStorage.getItem("tw_token") || localStorage.getItem("tw_token"))
        ) {
            _redirecting401 = true;
            // Aug-2026 (User-Wunsch): abgelaufener Token → automatisch zum Login
            // (auch bei 7-Tage-Login, dessen Token in localStorage liegt)
            try { sessionStorage.removeItem("tw_token"); } catch { /* noop */ }
            try { localStorage.removeItem("tw_token"); } catch { /* noop */ }
            window.location.href = "/login?expired=1";
        }
        return Promise.reject(error);
    }
);

export default api;

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Etwas ist schiefgelaufen.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => {
                if (!e || typeof e !== "object") return String(e ?? "");
                const field = Array.isArray(e.loc) ? e.loc.filter((l) => l !== "body").join(".") : "";
                const msg = typeof e.msg === "string" ? e.msg : JSON.stringify(e);
                return field ? `${field}: ${msg}` : msg;
            })
            .filter(Boolean)
            .join(" · ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    try { return JSON.stringify(detail); } catch { return "Etwas ist schiefgelaufen."; }
}

export function formatPrice(price) {
    if (price == null) return "—";
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);
}

// Jul-2026 (User-Wunsch) — Beträge im Verkaufsprozess IMMER mit 2 Nachkommastellen.
export function formatPriceExact(price) {
    if (price == null) return "—";
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
}

export function formatMileage(km) {
    if (km == null) return "—";
    return new Intl.NumberFormat("de-DE").format(km) + " km";
}

export function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("de-DE", { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return iso;
    }
}

/**
 * Phase 4.19 — Erstzulassungs- und Stammdaten-Datumsformat.
 * Akzeptiert ISO ("2022-05-01"), Date oder reines Jahr und liefert
 * immer „DD.MM.YYYY" zurück — bei reinem Jahr nur „YYYY".
 */
export function formatEzDate(value) {
    if (value == null || value === "") return "—";
    const s = String(value).trim();
    // Reines Jahr (Zahl oder "2022")
    if (/^\d{4}$/.test(s)) return s;
    // Nur Monat+Jahr bekannt (z. B. "2024-03") → "03.2024" (kein fiktiver Tag)
    const ym = s.match(/^(\d{4})-(\d{2})$/);
    if (ym) return `${ym[2]}.${ym[1]}`;
    try {
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return s;
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    } catch {
        return s;
    }
}
